import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  ModalFooter,
} from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import {
  LICENSE_STATUS_LABELS,
  LICENSE_TYPE_LABELS,
  isDesignPartner,
  licenseBadgeVariant,
  getEntitlements,
  getUsage,
  issueInstanceToken,
  listLicenseInstances,
  listLicensesForOrg,
  revokeLicenseInstance,
  type InstanceState,
  type IssuedInstanceToken,
  type LicenseInstance,
} from "../lib/api/license";
import { listApps } from "../lib/api/catalog";

export function LicensesRoute() {
  const organizationId = useCurrentOrganizationId();
  // UX only: the instance endpoints are platform-admin only on the server,
  // which re-checks every call. Non-admins simply do not see the controls.
  const isPlatformAdmin = useAuthStore((s) => !!s.user?.isPlatformAdmin);

  const licensesQuery = useQuery({
    queryKey: ["licenses", organizationId],
    queryFn: () => listLicensesForOrg(organizationId, { limit: 100, offset: 0 }),
    enabled: !!organizationId,
  });

  // Apps are looked up so we can show a human-readable name/icon next to
  // each license instead of just a raw app_id.
  const appsQuery = useQuery({
    queryKey: ["apps-for-licenses"],
    queryFn: () => listApps({ limit: 200, offset: 0 }),
    enabled: !!organizationId,
  });

  // Optional extra: a failure just hides the instances section.
  const entitlementsQuery = useQuery({
    queryKey: ["entitlements", organizationId],
    queryFn: () => getEntitlements(organizationId),
    enabled: !!organizationId,
    retry: false,
  });

  const usageQuery = useQuery({
    queryKey: ["usage", organizationId],
    queryFn: () => getUsage(organizationId, 30),
    enabled: !!organizationId,
    retry: false,
  });

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Set an organization_id in the dev auth panel above to view licenses."
      />
    );
  }

  if (licensesQuery.isLoading) {
    return <LoadingState message="Loading licenses..." />;
  }

  if (licensesQuery.isError) {
    return (
      <Alert variant="danger" title="Failed to load licenses">
        {apiErrorMessage(licensesQuery.error)}
      </Alert>
    );
  }

  const licenses = licensesQuery.data?.licenses ?? [];
  const appsById = new Map((appsQuery.data?.apps ?? []).map((a) => [a.id, a]));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">My Licenses</h1>

      {licenses.length === 0 ? (
        <EmptyState
          title="No licenses"
          description="This organization does not have any licensed apps yet. A staff admin can issue one from the Staff: Organizations area."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {licenses.map((license) => {
            const app = appsById.get(license.appId);
            return (
              <Card key={license.id} padding="lg">
                <CardBody>
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-medium">{app?.name ?? license.appId}</h2>
                      <p className="text-xs text-[var(--color-neutral-500)]">
                        Tier: {license.tier || "-"}
                      </p>
                    </div>
                    <Badge variant={licenseBadgeVariant(license.status)}>
                      {LICENSE_STATUS_LABELS[license.status]}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-[var(--color-neutral-500)]">
                    <span>License ID</span>
                    <span className="break-all font-mono" data-testid="license-id">
                      {license.id}
                    </span>
                    <CopyButton value={license.id} label="Copy license ID" />
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-[var(--color-neutral-600)]">
                    <p>{LICENSE_TYPE_LABELS[license.licenseType]}</p>
                    {isDesignPartner(license) && (
                      <div className="rounded border border-[var(--color-primary-200)] bg-[var(--color-primary-50)] p-2 text-xs text-[var(--color-primary-700)]">
                        Design partner license.
                        {license.designPartnerTrialEndsAt && (
                          <>
                            {" "}
                            Trial ends{" "}
                            {new Date(license.designPartnerTrialEndsAt).toLocaleDateString()}.
                          </>
                        )}
                        {license.designPartnerDiscountPercent > 0 && (
                          <> Converted with {license.designPartnerDiscountPercent}% discount.</>
                        )}
                      </div>
                    )}
                  </div>
                  {isPlatformAdmin && <LicenseInstancesPanel licenseId={license.id} />}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {(usageQuery.data ?? []).some((u) => Object.keys(u.totals).length > 0) && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-1 text-lg font-semibold">Usage</h2>
            <p className="mb-3 text-xs text-[var(--color-neutral-500)]">
              Counters your deployed apps report about themselves (for example active users), never your
              data. The latest value across your live instances.
            </p>
            <div className="space-y-3">
              {(usageQuery.data ?? [])
                .filter((u) => Object.keys(u.totals).length > 0)
                .map((u) => (
                  <div key={u.license_id}>
                    <p className="text-sm font-medium">{u.app.name ?? u.app.id}</p>
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
                      {Object.entries(u.totals)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([metric, value]) => (
                          <div key={metric} className="flex justify-between gap-2">
                            <dt className="font-mono text-xs text-[var(--color-neutral-500)]">{metric}</dt>
                            <dd>{value.toLocaleString()}</dd>
                          </div>
                        ))}
                    </dl>
                  </div>
                ))}
            </div>
          </CardBody>
        </Card>
      )}

      {(entitlementsQuery.data ?? []).some((e) => e.instances.length > 0) && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-1 text-lg font-semibold">Deployed instances</h2>
            <p className="mb-3 text-xs text-[var(--color-neutral-500)]">
              Private deployments report in about every 6 hours. This shows license status and
              heartbeat only, never your data.
            </p>
            <div className="space-y-4">
              {(entitlementsQuery.data ?? [])
                .filter((e) => e.instances.length > 0)
                .map((e) => (
                  <div key={e.license_id}>
                    <p className="text-sm font-medium">
                      {e.app.name ?? e.app.id}{" "}
                      <span className="text-xs font-normal text-[var(--color-neutral-500)]">
                        {e.effective_status}
                        {e.valid_until ? `, until ${new Date(e.valid_until).toLocaleDateString()}` : ""}
                        {e.seats != null ? `, ${e.seats} seats` : ""}
                      </span>
                    </p>
                    <ul className="mt-1 space-y-1 text-sm">
                      {e.instances.map((i) => (
                        <li key={i.id} className="flex items-center gap-2">
                          <Badge variant={INSTANCE_BADGE[i.state]}>{i.state.replace("_", " ")}</Badge>
                          <span>{i.name || i.id}</span>
                          <span className="text-xs text-[var(--color-neutral-500)]">
                            {i.last_seen_at ? `last seen ${new Date(i.last_seen_at).toLocaleString()}` : "not seen yet"}
                            {i.last_version ? `, version ${i.last_version}` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

const INSTANCE_BADGE: Record<InstanceState, "success" | "warning" | "danger" | "default"> = {
  online: "success",
  stale: "warning",
  offline: "danger",
  never_seen: "default",
  revoked: "danger",
};

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false); // clipboard blocked: the value is selectable on screen
    }
  }

  return (
    <Button size="sm" variant="secondary" aria-label={label} onClick={copy}>
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function LicenseInstancesPanel({ licenseId }: { licenseId: string }) {
  const queryClient = useQueryClient();
  const [instanceName, setInstanceName] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState<string | null>(null);
  // The one-time token lives only here. It is never put in the query cache,
  // storage or the URL, and is dropped when the dialog closes.
  const [issued, setIssued] = useState<IssuedInstanceToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<LicenseInstance | null>(null);

  const instancesQuery = useQuery({
    queryKey: ["license-instances", licenseId],
    queryFn: () => listLicenseInstances(licenseId),
    retry: false,
  });

  const revokeMutation = useMutation({
    mutationFn: (instanceId: string) => revokeLicenseInstance(licenseId, instanceId),
    onSuccess: () => {
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["license-instances", licenseId] });
      void queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    },
  });

  async function issue() {
    setIssuing(true);
    setIssueError(null);
    try {
      setIssued(await issueInstanceToken(licenseId, instanceName.trim()));
      setInstanceName("");
      void queryClient.invalidateQueries({ queryKey: ["license-instances", licenseId] });
      void queryClient.invalidateQueries({ queryKey: ["entitlements"] });
    } catch (err) {
      setIssueError(apiErrorMessage(err));
    } finally {
      setIssuing(false);
    }
  }

  const instances = instancesQuery.data ?? [];

  return (
    <div className="mt-4 space-y-3 border-t border-[var(--color-neutral-200)] pt-3">
      <h3 className="text-sm font-medium">Instances</h3>

      {instancesQuery.isLoading && <p className="text-xs text-[var(--color-neutral-500)]">Loading instances...</p>}
      {instancesQuery.isError && (
        <Alert variant="danger" title="Failed to load instances">
          {apiErrorMessage(instancesQuery.error)}
        </Alert>
      )}
      {instancesQuery.isSuccess && instances.length === 0 && (
        <p className="text-xs text-[var(--color-neutral-500)]">No instances have been issued a token.</p>
      )}
      {instances.length > 0 && (
        <ul className="space-y-2 text-sm">
          {instances.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2">
              <Badge variant={i.revoked ? "danger" : "success"}>{i.revoked ? "revoked" : "active"}</Badge>
              <span>{i.name || i.id}</span>
              <span className="text-xs text-[var(--color-neutral-500)]">
                {i.last_seen_at ? `last seen ${new Date(i.last_seen_at).toLocaleString()}` : "not seen yet"}
                {i.last_version ? `, version ${i.last_version}` : ""}
              </span>
              {!i.revoked && (
                <Button size="sm" variant="danger" onClick={() => setRevokeTarget(i)}>
                  Revoke instance
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <Input
          label="Instance name"
          value={instanceName}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setInstanceName(e.target.value)}
          placeholder="acme-prod"
        />
        <Button variant="primary" loading={issuing} onClick={issue}>
          Issue instance token
        </Button>
      </div>
      {issueError && (
        <Alert variant="danger" title="Could not issue a token">
          {issueError}
        </Alert>
      )}

      <Modal open={!!issued} onClose={() => setIssued(null)} title="Instance token" size="lg">
        {issued && (
          <div className="space-y-3">
            <Alert variant="warning" title="Copy this token now. It cannot be shown again.">
              Store it as a secret for the instance (for example a Kubernetes Secret). Closing this dialog
              discards it; if you lose it, issue a new token.
            </Alert>
            <div
              className="break-all rounded bg-[var(--color-neutral-100)] p-2 font-mono text-xs"
              data-testid="one-time-token"
            >
              {issued.token}
            </div>
            <p className="text-xs text-[var(--color-neutral-500)]">
              Instance {issued.instance_id}, first token expires {new Date(issued.expires_at).toLocaleString()}. The
              instance renews it automatically.
            </p>
          </div>
        )}
        <ModalFooter>
          {issued && <CopyButton value={issued.token} label="Copy token" />}
          <Button variant="primary" onClick={() => setIssued(null)}>
            I have stored it
          </Button>
        </ModalFooter>
      </Modal>

      <Modal open={!!revokeTarget} onClose={() => setRevokeTarget(null)} title="Revoke instance" size="sm">
        {revokeTarget && (
          <p className="text-sm">
            Revoke <span className="font-mono">{revokeTarget.name || revokeTarget.id}</span>? It stops renewing and
            runs out its current token. This cannot be undone; issue a new token to replace it.
          </p>
        )}
        {revokeMutation.isError && (
          <Alert variant="danger" title="Could not revoke">
            {apiErrorMessage(revokeMutation.error)}
          </Alert>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setRevokeTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={revokeMutation.isPending}
            onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
          >
            Revoke
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
